// Customer & Addresses Review (approved HTML:
// 14-Customer-Addresses-Consistency-Review-v5.html).
//
// Draft-order-only scope must not alter the customer default or any
// subscription. Save & Return preserves caller, work, channel and
// parent. Live writes gate on HUBSPOT_CUSTOMER_ADDRESS_SCOPE.
//
// W31 (2026-10-20): on a real session the screen maps the REAL body of
// GET /v1/contacts/:id/addresses (`addresses[]`, MEASURED in the W30
// audit) instead of dereferencing the fixture keys that crashed the
// screen ("Cannot read properties of undefined (reading '0')"). The
// identity row, breadcrumb and customer form come from the HubSpot
// profile; the SARAH fixture renders only in preview / dev-mock mode.
//
// W40.1 (2026-10-21, M3): the approved footer's first control, "Save &
// Return" (`button#ca-save-return.cx-save-return`, READ from 14 v5), is
// back. In preview it does what the approved script does: a dirty form
// is saved (locally) and the screen returns to the caller; otherwise it
// just returns. On a real session it stays VISIBLE and inert behind the
// live-write gate with the shared gate copy in its tooltip and in the
// footer note, never hidden (handoff decision 7 keeps it distinct from
// Finish & Next).
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { Icon } from '../_shared/IconSprite.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { RepriorityPill } from '../_shared/PriorityFocus.js';
import { CustomerDetailsForm } from './components/CustomerDetailsForm.js';
import { SavedAddressesCard } from './components/SavedAddressesCard.js';
import { AddressScopeCard, type ScopeKey } from './components/AddressScopeCard.js';
import {
  CUSTOMER_FORM,
  SAVED_ADDRESSES,
  SCOPE_LABELS,
  SUBSCRIPTIONS,
  type SavedAddress,
  type SubscriptionTarget,
} from './mockData.js';
import { SARAH } from '../../data/mockData.js';
import { getReviewReturnTarget } from '../_shared/ReviewState.js';
import { useScopedData } from '../_w2a_lower/scopedData.js';
import { isRecord, readString, useScreenIdentity, type ScreenIdentity } from '../_w2a_lower/identity.js';
import { LIVE_WRITE_PAUSED_LABEL } from '../_w2a_lower/liveWrite.js';
import './styles.css';

export const SAVE_RETURN_LABEL = 'Save & Return';
/** Footer note on a real session: the gate copy shared with every other inert write. */
export const SAVE_RETURN_PAUSED_NOTE = `${SAVE_RETURN_LABEL}: ${LIVE_WRITE_PAUSED_LABEL} · Finish & Next returns to the current action`;

// Scope isolation: profile addresses, current-order address, and
// subscription addresses are separately scoped reads. The production
// endpoint returns all three for atomic consistency so the rep sees the
// draft-order change next to the untouched profile / subscription record.
export interface CustomerAddressesPayload {
  customerForm: typeof CUSTOMER_FORM;
  savedAddresses: SavedAddress[];
  subscriptions: SubscriptionTarget[];
}

const CUSTOMER_ADDRESSES_FIXTURE: CustomerAddressesPayload = {
  customerForm: CUSTOMER_FORM,
  savedAddresses: SAVED_ADDRESSES,
  subscriptions: SUBSCRIPTIONS,
};

// Scope copy for a real session. The fixture copy names draft order
// #D-1048, which only exists in the design walkthrough.
const REAL_SCOPE_LABELS: typeof SCOPE_LABELS = {
  order: {
    heading: 'Only the open draft order changes.',
    detail: 'The customer default and every subscription keep their current addresses.',
  },
  default: {
    heading: 'The customer default changes for future orders.',
    detail: 'Existing orders and subscription schedules keep their current addresses.',
  },
  subscription: {
    heading: 'Only the selected subscription changes.',
    detail: 'The open draft order and the customer default keep their current addresses.',
  },
};

function addressLabel(source: string, index: number): string {
  if (source === 'hubspot_default') return 'Default address';
  if (source.length > 0) return source.replace(/[_-]+/g, ' ');
  return `Address ${index + 1}`;
}

/**
 * Map the REAL api-server body to the screen payload.
 *
 * MEASURED 2026-10-20, GET /v1/contacts/573130771181/addresses:
 *   { result:'OK', contactId, addresses:[{ addrId:'default',
 *     source:'hubspot_default', line1:'21 Dracut Hill Rd', line2:null,
 *     city:'Vaughn', state:'Montana', zip:'59487', country:'United States' }] }
 *
 * Anything that is not that shape maps to an empty list (honest empty),
 * never to the fixture. Subscriptions have no read source yet, so the
 * list is empty on every real session.
 */
export function mapRealAddresses(body: unknown, identity: ScreenIdentity): CustomerAddressesPayload {
  const customer = identity.customer;
  const profile = identity.profile;
  const customerForm = {
    first: profile?.firstName ?? '',
    last: profile?.lastName ?? '',
    email: profile?.email ?? '',
    phone: profile?.phone ?? '',
    customerId: profile?.id ?? customer.id,
  };
  const rawList = isRecord(body) && Array.isArray(body.addresses) ? body.addresses : [];
  const savedAddresses: SavedAddress[] = [];
  rawList.forEach((raw, index) => {
    if (!isRecord(raw)) return;
    const id = readString(raw.addrId) || `address-${index + 1}`;
    const line1 = readString(raw.line1);
    const line2 = readString(raw.line2);
    savedAddresses.push({
      id,
      label: addressLabel(readString(raw.source), index),
      recipient: customer.fullName,
      line1: line2 ? `${line1}, ${line2}` : line1,
      city: readString(raw.city),
      state: readString(raw.state),
      zip: readString(raw.zip),
      current: index === 0,
    });
  });
  return { customerForm, savedAddresses, subscriptions: [] };
}

export interface CustomerAddressesScreenProps {
  stateOverride?: BoundaryState;
}

export function CustomerAddressesScreen({
  stateOverride,
}: CustomerAddressesScreenProps = {}): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const customerId = params.customerId ?? SARAH.id;
  const identity = useScreenIdentity(customerId);
  const scoped = useScopedData<unknown>({
    path: `/v1/contacts/${encodeURIComponent(customerId)}/addresses`,
    fixture: CUSTOMER_ADDRESSES_FIXTURE,
    loadingLabel: 'Loading customer addresses…',
    errorTitle: 'Addresses could not load',
    errorCode: 'CUSTOMER_ADDRESSES_UNAVAILABLE',
    // A 200 with zero addresses still renders the customer details; the
    // honest empty lives inside the saved-addresses card.
    isEmpty: () => false,
    ...(stateOverride !== undefined ? { stateOverride } : {}),
  });
  const useFixture = scoped.previewAllowed;
  const payload = useMemo<CustomerAddressesPayload>(
    () => (useFixture ? CUSTOMER_ADDRESSES_FIXTURE : mapRealAddresses(scoped.data, identity)),
    [useFixture, scoped.data, identity],
  );
  const [values, setValues] = useState(payload.customerForm);
  const [selectedAddr, setSelectedAddr] = useState(payload.savedAddresses[0]?.id ?? '');
  const [scope, setScope] = useState<ScopeKey>('order');
  const [subscriptionId, setSubscriptionId] = useState(payload.subscriptions[0]?.id ?? '');
  // Re-seed the form whenever the authoritative payload changes (real
  // body or profile arriving after first paint).
  useEffect(() => {
    setValues(payload.customerForm);
    setSelectedAddr(payload.savedAddresses[0]?.id ?? '');
    setSubscriptionId(payload.subscriptions[0]?.id ?? '');
  }, [payload]);
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const dirty = useMemo(() => {
    return (
      values.first !== payload.customerForm.first ||
      values.last !== payload.customerForm.last ||
      values.email !== payload.customerForm.email ||
      values.phone !== payload.customerForm.phone
    );
  }, [values, payload.customerForm]);
  const state =
    stateOverride ??
    (scoped.state.kind === 'default' ? resolveState(kind, undefined) : scoped.state);
  const selected: SavedAddress | null =
    payload.savedAddresses.find(a => a.id === selectedAddr) ??
    payload.savedAddresses[0] ??
    null;
  const returnTarget = getReviewReturnTarget(location.state, customerId);
  const customer = identity.customer;
  const paused = identity.liveWritesPaused;
  const returnToCaller = (): void => navigate(returnTarget.path, { state: returnTarget.state });
  // Approved behaviour (14 v5 `#ca-save-return`): a dirty form is saved
  // first, then the screen returns; a clean one just returns. Inert on a
  // real session (the gate), so it never reaches here while paused.
  const handleSaveReturn = (): void => {
    if (paused) return;
    if (dirty) setValues(values);
    returnToCaller();
  };
  const saveReturnControl = (
    <button
      type="button"
      id="ca-save-return"
      className="cx-save-return"
      data-testid="ca-save-return"
      disabled={paused}
      aria-disabled={paused || undefined}
      data-live-write-paused={paused || undefined}
      aria-describedby="finish-reason"
      title={paused ? `${SAVE_RETURN_LABEL}: ${LIVE_WRITE_PAUSED_LABEL}` : undefined}
      onClick={handleSaveReturn}
    >
      Save &amp; Return
    </button>
  );

  return (
    <WorkspaceChrome screen="customer-addresses" rail="my-work">
      {/* W40: the approved 14 has no breadcrumb row (MEASURED, 0 entries);
          the row stays only as the carrier of the preview label. */}
      {scoped.previewAllowed ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>{customer.fullName}</strong>{' '}
            <span className="slash">/</span> Customer &amp; Addresses
          </div>
          <span className="preview">Design preview · Sample customer</span>
        </div>
      ) : null}
      <section className="ca-hero">
        <div>
          <span className="eyebrow">CUSTOMER RECORD</span>
          <h1>Customer &amp; Addresses</h1>
          <p>
            Keep the customer’s details current. Apply a delivery change exactly where it belongs.
          </p>
        </div>
        <div className="ca-hero-cue">
          <Icon id="pin" />
          <div>
            <b>Choose the scope before saving</b>
            <span>One order · Customer default · One subscription</span>
          </div>
        </div>
        {/* W40: approved pill, informational, on every session. */}
        <div className="shared-repriority-heading">
          <RepriorityPill className="shared-repriority" />
        </div>
      </section>
      <IdentityRow
        preferenceNote="Changing a contact detail keeps permissions intact."
        customer={customer}
        badgeLabel="Customer details"
        {...(identity.sinceLabel !== undefined ? { sinceLabel: identity.sinceLabel } : {})}
      />
      <DataBoundary state={state} testId="customer-addresses-boundary">
        <div id="cx-original-surface">
          <div className="ca-section-line">
            <h2>Details &amp; delivery</h2>
            <span id="ca-save-state">
              <i /> {dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
            <button className="text-btn" id="ca-history" type="button">
              View change history <Icon id="arrow" />
            </button>
          </div>
          <div className="ca-grid">
            <div className="ca-stack">
              <CustomerDetailsForm
                form={payload.customerForm}
                values={values}
                onChange={setValues}
                dirty={dirty}
                onDiscard={() => setValues(payload.customerForm)}
                onSave={() => setValues(values)}
                liveWritesPaused={paused}
              />
              <SavedAddressesCard
                addresses={payload.savedAddresses}
                activeId={selectedAddr}
                onSelect={setSelectedAddr}
                onEdit={() => undefined}
                onAdd={() => undefined}
                liveWritesPaused={paused}
              />
            </div>
            <aside className="ca-stack">
              <AddressScopeCard
                selected={selected}
                scope={scope}
                onScopeChange={setScope}
                subscriptions={payload.subscriptions}
                subscriptionId={subscriptionId}
                onSubscriptionChange={setSubscriptionId}
                onReview={() => undefined}
                scopeCopy={useFixture ? SCOPE_LABELS : REAL_SCOPE_LABELS}
                draftOrderLabel={useFixture ? 'Draft order #D-1048' : 'No draft order open'}
                liveWritesPaused={paused}
              />
              <section className="ca-callout">
                <div className="ca-callout-icon">
                  <Icon id="check" />
                </div>
                <div>
                  <strong>A change with a clear destination</strong>
                  <p>
                    A new default applies to future use. Existing orders and subscription
                    schedules change only when selected.
                  </p>
                </div>
              </section>
            </aside>
          </div>
          <section className="next ca-next" aria-label="Next action">
            <span className="eyebrow">UP NEXT</span>
            <div className="avatar">{customer.initials}</div>
            <div>
              <strong>Return to {identity.possessive} current action</strong>
              <p>Continue the conversation with the updated details.</p>
            </div>
            <div className="reason">
              <span>Same customer</span>
              <p>Keep the work moving</p>
            </div>
            <Icon id="arrow" />
          </section>
        </div>
      </DataBoundary>
      <ActionFooter
        leading={saveReturnControl}
        disabled={dirty}
        noteWhenDisabled="Save or discard pending changes first"
        noteWhenEnabled={paused ? SAVE_RETURN_PAUSED_NOTE : 'All changes saved · Ready to return'}
        summaryStrong={`${customer.fullName} · Customer details`}
        summaryBody="Saved addresses change only the scope you select."
        onFinish={returnToCaller}
      />
      {scoped.previewAllowed && !stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Loading customer record…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No saved addresses on file',
        body: 'Add the first address to apply it as the default or to the active order.',
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Customer record did not load',
        detail: 'The HubSpot contact fetch did not respond.',
        errorCode: 'CUSTOMER_RECORD_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'saved-addresses', reason: 'address_cache_stale' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Address scope save is paused',
        reason: 'Live-write gate OFF for Customer Address scope.',
        gateName: 'HUBSPOT_CUSTOMER_ADDRESS_SCOPE',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Applying address change…',
        actionName: 'customer_address_apply',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default CustomerAddressesScreen;
